import * as React from 'react'
import { Link2, Loader2 } from 'lucide-react'
import type { HcpAddressDto } from '@lib/dto/housecall-pro.dto'
import { hcpAddressKindWords } from '@lib/enums/hcp-address-kind.enum'
import type { SiteDto } from '@lib/dto/site.dto'
import { matchingHcpAddresses } from '@lib/helpers/hcp-addresses'
import { hcpAddressOfSite, type HcpAddressDraft } from '@lib/helpers/hcp-customers'
import {
  useCreateHcpAddress,
  useHcpCustomer,
  useLinkHcpAddress,
} from '@/hooks/useClientHcp'
import { errorMessage } from '@/helpers/logger'
import { StatusPill } from '@/components/StatusPill'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

function draftAddressLine(address: HcpAddressDraft): string {
  const street = [address.street, address.streetLine2]
    .filter((part): part is string => part !== null)
    .join(', ')
  return `${street}, ${address.city}, ${address.state} ${address.zip}`
}

/** The parts of an HCP address after its label: "TX 79424 · service". */
function addressDetail(address: HcpAddressDto): string {
  const kind = address.kind === null ? null : hcpAddressKindWords(address.kind)
  return [[address.state, address.zip].filter(Boolean).join(' '), kind]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ')
}

interface HcpAddressLinkProps {
  site: SiteDto
  /** The client's HCP customer, whose addresses the site chooses among. */
  customer: { id: string; name: string }
  writesEnabled: boolean
}

/**
 * The Housecall Pro address a site's jobs go to, in the site's row. Opens a
 * dialog choosing among the client's customer's addresses.
 */
export function HcpAddressLink({ site, customer, writesEnabled }: HcpAddressLinkProps) {
  const [open, setOpen] = React.useState<boolean>(false)

  return (
    <>
      <button
        type="button"
        className="-mx-1.5 inline-flex max-w-56 items-center gap-1.5 rounded px-1.5 py-0.5 text-left transition-colors hover:bg-muted"
        onClick={() => {
          setOpen(true)
        }}
      >
        {site.hcpAddress === null ? (
          <span className="text-muted-text italic">Not linked</span>
        ) : (
          <>
            <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-text" />
            <span className="truncate text-secondary-text">{site.hcpAddress.label}</span>
          </>
        )}
      </button>
      {open ? (
        <HcpAddressDialog
          site={site}
          customer={customer}
          writesEnabled={writesEnabled}
          onClose={() => {
            setOpen(false)
          }}
        />
      ) : null}
    </>
  )
}

interface HcpAddressDialogProps extends HcpAddressLinkProps {
  onClose: () => void
}

/**
 * Choose the site's address on the customer, read live from HCP.
 *
 * The one that reads as the site's own address is marked, never chosen on its
 * own: two addresses on one street read alike, and a job sent to the wrong
 * one sends a crew there. When none is the site's, the site's address can be
 * added to the customer, after saying where it is going.
 */
function HcpAddressDialog({ site, customer, writesEnabled, onClose }: HcpAddressDialogProps) {
  const hcpCustomer = useHcpCustomer(customer.id)
  const linkAddress = useLinkHcpAddress()
  const createAddress = useCreateHcpAddress()

  const [confirmingCreate, setConfirmingCreate] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)

  const busy = linkAddress.isPending || createAddress.isPending
  const siteAddress = hcpAddressOfSite(site)
  const addresses = hcpCustomer.data?.addresses ?? []
  const matches = matchingHcpAddresses(
    {
      street: site.addressLine1,
      city: site.city,
      state: site.state,
      zip: site.postalCode,
    },
    addresses,
  )
  const match = matches.length === 1 ? (matches[0] ?? null) : null

  async function handleLink(addressId: string | null): Promise<void> {
    setError(null)
    try {
      await linkAddress.mutateAsync({ siteId: site.id, clientId: site.clientId, addressId })
      onClose()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleCreate(): Promise<void> {
    setError(null)
    try {
      await createAddress.mutateAsync({ siteId: site.id, customerId: customer.id })
      onClose()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  function renderAddresses() {
    if (hcpCustomer.isPending) {
      return (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (hcpCustomer.isError) {
      return (
        <p className="px-3 py-6 text-center text-sm text-destructive">
          {errorMessage(hcpCustomer.error, 'Something went wrong. Please try again.')}
        </p>
      )
    }
    if (addresses.length === 0) {
      return (
        <p className="px-3 py-6 text-center text-sm text-muted-text">
          {customer.name} has no addresses in Housecall Pro.
        </p>
      )
    }
    return (
      <ul className="divide-y divide-default">
        {addresses.map((address) => (
          <li
            key={address.id}
            className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-subtle"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{address.label}</p>
              <p className="truncate text-xs text-muted-text">{addressDetail(address)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {match?.id === address.id ? (
                <StatusPill tone="green">This site’s address</StatusPill>
              ) : null}
              {site.hcpAddress?.id === address.id ? (
                <span className="text-xs text-muted-text">Linked</span>
              ) : (
                <Button
                  size="sm"
                  variant={match?.id === address.id ? 'default' : 'outline'}
                  disabled={busy}
                  onClick={() => {
                    void handleLink(address.id)
                  }}
                >
                  Link
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    )
  }

  function renderCreate() {
    // Only once the customer has answered, and only when none of its
    // addresses is already this one: adding a second copy is how a crew is
    // sent to an address nobody maintains. Several matches is not "none" —
    // `matchingHcpAddress` returns null for both, and offering to create in
    // the several case adds yet another copy of a street that is already there.
    if (!hcpCustomer.isSuccess || site.hcpAddress !== null) {
      return null
    }
    if (matches.length > 1) {
      return (
        <p className="text-xs text-muted-text">
          More than one of these addresses reads as this site. Link the right
          one rather than adding another.
        </p>
      )
    }
    if (match !== null) return null
    if (siteAddress === null) {
      return (
        <p className="text-xs text-muted-text">
          Not there? Give the site a street, city, state and ZIP to add it to
          Housecall Pro.
        </p>
      )
    }
    if (!writesEnabled) {
      return (
        <p className="text-xs text-muted-text">
          Not there? Adding addresses is switched off in Settings. Add it to
          the customer in Housecall Pro, then link it here.
        </p>
      )
    }
    if (!confirmingCreate) {
      return (
        <p className="text-xs text-muted-text">
          Not there?{' '}
          <button
            type="button"
            className="font-medium text-brand underline-offset-4 hover:underline"
            onClick={() => {
              setError(null)
              setConfirmingCreate(true)
            }}
          >
            Add the site’s address to {customer.name}
          </button>
        </p>
      )
    }
    return (
      <div className="space-y-2 rounded-md border border-default bg-subtle px-3 py-2">
        <p className="text-sm">
          Add <span className="font-medium">{draftAddressLine(siteAddress)}</span> to{' '}
          {customer.name} in the live Housecall Pro account?
        </p>
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => {
              setConfirmingCreate(false)
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              void handleCreate()
            }}
          >
            {createAddress.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Add address
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{site.name} in Housecall Pro</DialogTitle>
          <DialogDescription>
            The site’s jobs go to this address on {customer.name}.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-secondary-text">
          This site:{' '}
          {siteAddress === null ? (
            <span className="text-muted-text italic">no complete address recorded</span>
          ) : (
            draftAddressLine(siteAddress)
          )}
        </p>

        <div className="max-h-72 overflow-y-auto rounded-md border border-default">
          {renderAddresses()}
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {renderCreate()}

        {site.hcpAddress === null ? null : (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                void handleLink(null)
              }}
            >
              Unlink
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
